/**
 * Native Chat-Komponente für den KI-Assistent-Drawer.
 *
 * Theme-Tokens (bg-card, text-foreground, border-default, text-muted-foreground, bg-muted)
 * statt expliziter Light/Dark-Farben — Portal-Theme schaltet via data-mode am
 * <html>-Element zwischen Hell/Dunkel um.
 */
import { authFetch, useAuth } from '@onvice/portal-shell-api';

import { useState, useRef, useEffect, useCallback } from 'react';
import { Send, Loader2, Wrench, Trash2, Plus, Brain, Paperclip, X as XIcon } from 'lucide-react';

import { useAssistantState } from '../context/AssistantStateContext';

// Anhang im Frontend-State: id vom Backend, dazu Mime + ein DataURL-Thumbnail
// fuer die Vorschau im Eingabe-Bereich. Maximum 4 Bilder pro Turn (Backend-Limit).
interface PendingAttachment {
  id: string;
  mime: string;
  size: number;
  thumbnail: string;
}
const ATTACHMENT_LIMIT = 4;
const ATTACHMENT_MAX_BYTES = 10 * 1024 * 1024;

// Default: same-origin via Portal-Module-Proxy. Lokale Dev kann ueber VITE_ASSISTANT_URL
// auf http://localhost:8090 zeigen (Direct-Access ohne Proxy).
// `||` statt `??` weil Vite ungesetzte ENV-Vars zu Leerstring kompiliert,
// und ?? greift nur bei null/undefined — `|| /api/...` deckt beides ab.
const ASSISTANT_BASE =
  (import.meta.env.VITE_ASSISTANT_URL as string | undefined)?.replace(/\/+$/, '') || '/api/modules/assistant';
const LS_CONV_ID = 'asst-conv-id';

interface Step {
  tool: string;
  args?: Record<string, unknown>;
  success?: boolean;
  preview?: string;
}

/** Tool-Slug → benutzerfreundliche Aktion (Status während der Ausführung). */
const TOOL_HUMAN_LABEL: Record<string, string> = {
  list_customers:        'Lade Kunden',
  list_tasks:            'Lade Aufgaben',
  list_reports:          'Lade Berichte',
  list_materials:        'Lade Materialien',
  list_templates:        'Lade Vorlagen',
  search_library:        'Durchsuche Bibliothek',
  query_database:        'Frage Datenbank ab',
  generate_from_template: 'Erstelle Entwurf aus Vorlage',
  get_current_date:      'Hole aktuelles Datum',
};

function humanizeStep(s: Step): string {
  const label = TOOL_HUMAN_LABEL[s.tool] ?? s.tool;
  if (s.success === undefined) return `${label}…`;
  if (s.success) return `${label} ✓`;
  return `${label} fehlgeschlagen`;
}

/** Animierter "denkt nach"-Indikator: Brain-Icon mit Pulse + Shimmer-Text. */
function ThinkingIndicator({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-2 text-sm">
      <Brain
        size={16}
        className="text-blue-500 animate-pulse"
        style={{ animationDuration: '1.4s' }}
      />
      <span
        className="bg-clip-text text-transparent font-medium"
        style={{
          backgroundImage:
            'linear-gradient(90deg, rgba(100,116,139,0.5) 0%, rgba(59,130,246,1) 50%, rgba(100,116,139,0.5) 100%)',
          backgroundSize: '200% 100%',
          animation: 'shimmer-text 2.2s linear infinite',
        }}
      >
        {label}
      </span>
      <style>{`@keyframes shimmer-text { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }`}</style>
    </div>
  );
}

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  steps?: Step[];
}

export interface SelectionRequiredEvent {
  template_slug: string;
  data_refs: Record<string, unknown>;
  sources: Record<string, {
    rows: Array<Record<string, unknown>>;
    group_by?: string;
    group_title_field?: string;
    id_field: string;
  }>;
  /** Optional: Entity-Disambig — Frontend zeigt zuerst Radio-Auswahl,
   *  der Re-Submit setzt data_refs[target_ref] = <gewaehlte-id>. */
  entity_choice?: {
    entity_type: 'customer' | 'material' | 'task' | 'report';
    target_ref: string;
    clear_ref?: string;
    query: string;
    options: Array<{ id: string; label: string; details: Record<string, string> }>;
    display_fields: string[];
  };
}

interface Props {
  /** Wird aufgerufen, wenn sich die conversation_id ändert (neu angelegt, gewechselt, gelöscht) */
  onConversationChange?: (id: string | null) => void;
  /** Wird beim SSE-Event `result_panel` aufgerufen — Drawer setzt den KI-Indikator + merkt sich für Auto-Switch */
  onPanelEvent?: (panelId: string) => void;
  /** Streaming-State an Drawer melden für KI-Indikator-Logik */
  onStreamingChange?: (streaming: boolean) => void;
  /** Vorlagen-Datenquelle mit selection_mode=manual: Frontend zeigt Modal */
  onSelectionRequired?: (ev: SelectionRequiredEvent) => void;
}

export default function AssistantChatPanel({ onConversationChange, onPanelEvent, onStreamingChange, onSelectionRequired }: Props = {}) {
  const { user } = useAuth();
  const mascotState = useAssistantState();
  // Bei aktiver Persona ist `user` bereits die Persona (siehe /auth/me Backend).
  // Wir senden die effektive Identitaet (Persona-ID, Persona-Rolle) konsistent —
  // alles andere fuehrt zu Inkonsistenz im Assistant (User-ID = Tester, Rolle =
  // Persona) und damit zu falschen Permission-Checks.
  const effectiveUserId = user?.id ?? '';
  const effectiveEmail = user?.email ?? '';
  const effectiveRole = user?.role ?? 'default';

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(() => localStorage.getItem(LS_CONV_ID));
  const [activeSteps, setActiveSteps] = useState<Step[]>([]);
  const [attachments, setAttachments] = useState<PendingAttachment[]>([]);
  const [uploadingAttachments, setUploadingAttachments] = useState(false);
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Conversation-ID beim Mount + bei Änderung an Drawer melden
  useEffect(() => {
    onConversationChange?.(conversationId);
  }, [conversationId, onConversationChange]);

  // Streaming-State an Drawer + Mascot-State melden.
  // Mascot-Logik: streaming startet → 'searching', endet → 'success' (wenn Panel kam) / 'idle'.
  // Creating wird im onPanelEvent-Handler (panel-Event) gesetzt.
  const prevStreaming = useRef(false);
  useEffect(() => {
    onStreamingChange?.(streaming);
    if (streaming && !prevStreaming.current) mascotState.beginStream();
    else if (!streaming && prevStreaming.current) mascotState.endStream();
    prevStreaming.current = streaming;
  }, [streaming, onStreamingChange, mascotState]);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, activeSteps, streaming]);

  // Auto-Focus aufs Eingabefeld beim Mount — Joerg-Wunsch 2026-05-26.
  // NUR auf Desktop (pointer:fine + Breite ≥768px), damit auf Mobile nicht
  // automatisch die On-Screen-Tastatur hochkommt und das Layout
  // zusammenstaucht — der User entscheidet dort selbst per Tap.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const isDesktop = window.matchMedia('(pointer: fine) and (min-width: 768px)').matches;
    if (isDesktop) inputRef.current?.focus();
  }, []);

  const newConversation = useCallback(() => {
    setConversationId(null);
    localStorage.removeItem(LS_CONV_ID);
    setMessages([]);
    setActiveSteps([]);
  }, []);

  const deleteConversation = useCallback(() => {
    if (messages.length === 0 && !conversationId) return;
    if (!window.confirm('Verlauf dieser Konversation löschen? Die alten Nachrichten sind danach weg.')) return;
    setConversationId(null);
    localStorage.removeItem(LS_CONV_ID);
    setMessages([]);
    setActiveSteps([]);
  }, [messages.length, conversationId]);

  // Verlauf der bestehenden Konversation beim Mount nachladen (Tester
  // 2026-05-15: „Nach Reload ist der Chat nicht mehr vorhanden"). Backend
  // hat den Verlauf — Frontend musste ihn nur ziehen.
  useEffect(() => {
    if (!conversationId || messages.length > 0) return;
    authFetch(`${ASSISTANT_BASE}/assistant/conversations/${conversationId}/messages`, {
      headers: {
        'X-Portal-User-Id': effectiveUserId,
        'X-Portal-User-Email': effectiveEmail,
        'X-Portal-User-Role': effectiveRole,
      },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        const rows: Array<{ id: string; role: 'user' | 'assistant' | 'system'; content: string }> = data?.data?.messages ?? [];
        if (rows.length === 0) return;
        setMessages(rows.map((m) => ({ id: m.id, role: m.role, content: m.content })));
      })
      .catch(() => { /* History-Load best-effort */ });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId]);

  // === Multimodal: Bild-Uploads ===
  //
  // Pipeline pro Bild:
  //  1) Datei aus Drop/Picker/Paste-Event → File-Objekt
  //  2) Local FileReader → DataURL-Thumbnail (Vorschau im Eingabefeld)
  //  3) POST /upload (multipart) → attachment_id vom Backend
  //  4) send() haengt attachment_ids an den /chat-Body
  const uploadFiles = useCallback(async (files: File[]) => {
    if (files.length === 0) return;
    const remaining = ATTACHMENT_LIMIT - attachments.length;
    const valid = files
      .filter((f) => f.type.startsWith('image/'))
      .filter((f) => f.size <= ATTACHMENT_MAX_BYTES)
      .slice(0, remaining);
    if (valid.length === 0) return;
    setUploadingAttachments(true);
    try {
      const fd = new FormData();
      for (const f of valid) fd.append('files', f);
      const resp = await authFetch(`${ASSISTANT_BASE}/assistant/upload`, {
        method: 'POST',
        headers: {
          // Multipart-Boundary setzt fetch selbst — Content-Type weglassen.
          'X-Portal-User-Id': effectiveUserId,
          'X-Portal-User-Email': effectiveEmail,
          'X-Portal-User-Role': effectiveRole,
        },
        body: fd,
      });
      if (!resp.ok) throw new Error(`Upload ${resp.status}`);
      const data = (await resp.json()) as {
        attachments: Array<{ id: string; mime: string; size_bytes: number }>;
      };
      // Thumbnails aus den File-Objekten — parallel zur Backend-Antwort
      // (FileReader laeuft lokal, kein Roundtrip).
      const thumbs = await Promise.all(
        valid.map(
          (f) =>
            new Promise<string>((resolve) => {
              const r = new FileReader();
              r.onload = () => resolve(r.result as string);
              r.onerror = () => resolve('');
              r.readAsDataURL(f);
            }),
        ),
      );
      setAttachments((prev) => [
        ...prev,
        ...data.attachments.map((a, i) => ({
          id: a.id,
          mime: a.mime,
          size: a.size_bytes,
          thumbnail: thumbs[i] ?? '',
        })),
      ]);
    } catch (e) {
      setMessages((prev) => [
        ...prev,
        {
          id: crypto.randomUUID(),
          role: 'system',
          content: `Bild-Upload fehlgeschlagen: ${(e as Error).message}`,
        },
      ]);
    } finally {
      setUploadingAttachments(false);
    }
  }, [attachments.length, effectiveUserId, effectiveEmail, effectiveRole]);

  const onPickFiles = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    void uploadFiles(files);
    // Reset, damit gleiche Datei nochmal gewaehlt werden kann
    e.target.value = '';
  }, [uploadFiles]);

  const onDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDraggingOver(false);
    const files = Array.from(e.dataTransfer.files ?? []);
    void uploadFiles(files);
  }, [uploadFiles]);

  const onPaste = useCallback((e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const items = Array.from(e.clipboardData?.items ?? []);
    const files = items
      .filter((it) => it.kind === 'file')
      .map((it) => it.getAsFile())
      .filter((f): f is File => f !== null);
    if (files.length > 0) {
      e.preventDefault();
      void uploadFiles(files);
    }
  }, [uploadFiles]);

  const removeAttachment = useCallback((id: string) => {
    setAttachments((prev) => prev.filter((a) => a.id !== id));
  }, []);

  const send = useCallback(async () => {
    const text = input.trim();
    if ((!text && attachments.length === 0) || streaming) return;
    // Bilder werden als Marker-Text in der Bubble angedeutet, damit der User
    // sieht dass das Bild zur Frage gehoert. Echte Vorschau im Chat-Verlauf
    // koennte spaeter ergaenzt werden — Backend speichert das base64 ohnehin.
    const displayText = attachments.length > 0
      ? `${text}${text ? '\n' : ''}[${attachments.length} ${attachments.length === 1 ? 'Bild' : 'Bilder'} angehängt]`
      : text;
    const userMsg: ChatMessage = { id: crypto.randomUUID(), role: 'user', content: displayText };
    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    setStreaming(true);
    setActiveSteps([]);
    inputRef.current?.focus();
    const attachmentIds = attachments.map((a) => a.id);
    setAttachments([]);

    try {
      const resp = await authFetch(`${ASSISTANT_BASE}/assistant/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Portal-User-Id': effectiveUserId,
          'X-Portal-User-Email': effectiveEmail,
          'X-Portal-User-Role': effectiveRole,
        },
        body: JSON.stringify({
          conversation_id: conversationId,
          message: text || '(siehe Bild)',
          attachment_ids: attachmentIds.length > 0 ? attachmentIds : undefined,
        }),
      });

      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        setMessages((prev) => [
          ...prev,
          { id: crypto.randomUUID(), role: 'system', content: `Fehler: ${err.message ?? resp.status}` },
        ]);
        return;
      }
      const newConv = resp.headers.get('X-Conversation-Id');
      if (newConv && newConv !== conversationId) {
        setConversationId(newConv);
        localStorage.setItem(LS_CONV_ID, newConv);
      }

      const reader = resp.body!.getReader();
      const dec = new TextDecoder();
      let buffer = '';
      const localSteps: Step[] = [];

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += dec.decode(value, { stream: true });
        const events = buffer.split('\n\n');
        buffer = events.pop() ?? '';
        for (const evt of events) {
          if (!evt.trim()) continue;
          const lines = evt.split('\n');
          const eventType = lines.find((l) => l.startsWith('event: '))?.slice(7).trim();
          const dataLine = lines.find((l) => l.startsWith('data: '))?.slice(6) ?? '{}';
          let data: Record<string, unknown> = {};
          try {
            data = JSON.parse(dataLine);
          } catch {
            /* ignore */
          }

          if (eventType === 'result_panel') {
            // Panel wurde im Backend angelegt — Drawer benachrichtigen + Mascot auf 'creating'
            onPanelEvent?.(String(data.panel_id ?? ''));
            mascotState.notePanel();
          } else if (eventType === 'tool_start') {
            localSteps.push({
              tool: String(data.tool ?? '?'),
              args: (data.args as Record<string, unknown>) ?? undefined,
            });
            setActiveSteps([...localSteps]);
            mascotState.noteToolStart();
          } else if (eventType === 'tool_result') {
            if (data._remove === true) {
              // Hard-Override (z.B. Customer-Resolution mit Vorschlägen): der
              // Step ist überflüssig — die finale Antwort spricht für sich.
              const idx = localSteps.findIndex((s) => s.tool === data.tool);
              if (idx !== -1) localSteps.splice(idx, 1);
            } else {
              const last = localSteps[localSteps.length - 1];
              if (last && last.tool === data.tool) {
                last.success = Boolean(data.success);
                last.preview = String(data.preview ?? '');
              }
            }
            setActiveSteps([...localSteps]);
            mascotState.noteToolEnd();
          } else if (eventType === 'selection_required') {
            // Vorlage mit selection_mode=manual ODER Entity-Disambig: Drawer
            // oeffnet das passende Modal anhand des entity_choice-Felds.
            onSelectionRequired?.({
              template_slug: String(data.template_slug ?? ''),
              data_refs: (data.data_refs as Record<string, unknown>) ?? {},
              sources: (data.sources as SelectionRequiredEvent['sources']) ?? {},
              ...(data.entity_choice
                ? { entity_choice: data.entity_choice as SelectionRequiredEvent['entity_choice'] }
                : {}),
            });
          } else if (eventType === 'done') {
            setMessages((prev) => [
              ...prev,
              {
                id: crypto.randomUUID(),
                role: 'assistant',
                content: String(data.text ?? ''),
                steps: [...localSteps],
              },
            ]);
            setActiveSteps([]);
          } else if (eventType === 'error') {
            setMessages((prev) => [
              ...prev,
              { id: crypto.randomUUID(), role: 'system', content: `Fehler: ${data.message}` },
            ]);
          }
        }
      }
    } catch (e) {
      const msg = (e as Error).message ?? 'unbekannt';
      setMessages((prev) => [
        ...prev,
        { id: crypto.randomUUID(), role: 'system', content: `Netzwerk-Fehler: ${msg}` },
      ]);
    } finally {
      setStreaming(false);
    }
  }, [input, streaming, conversationId, effectiveUserId, effectiveEmail, effectiveRole, onPanelEvent, mascotState]);

  const onKey = (e: React.KeyboardEvent<HTMLTextAreaElement>): void => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void send();
    }
  };

  return (
    <div className="flex flex-col h-full bg-card text-foreground">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-3 py-2 md:py-1.5 border-b border-default text-sm md:text-xs bg-muted">
        <span className="text-muted-foreground">
          {conversationId ? `Konversation ${conversationId.slice(0, 8)}` : 'neue Konversation'}
        </span>
        <div className="flex items-center gap-1">
          <button
            onClick={newConversation}
            title="Neuer Chat"
            aria-label="Neuer Chat"
            className="h-10 w-10 md:h-7 md:w-7 rounded hover:bg-card text-muted-foreground hover:text-foreground transition flex items-center justify-center"
          >
            <Plus size={18} className="md:w-[14px] md:h-[14px]" />
          </button>
          <button
            onClick={deleteConversation}
            title="Verlauf löschen"
            aria-label="Verlauf löschen"
            disabled={!conversationId && messages.length === 0}
            className="h-10 w-10 md:h-7 md:w-7 rounded hover:bg-card text-muted-foreground hover:text-foreground transition flex items-center justify-center disabled:opacity-30 disabled:hover:bg-transparent"
          >
            <Trash2 size={18} className="md:w-[14px] md:h-[14px]" />
          </button>
        </div>
      </div>

      {/* Verlauf */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-2 space-y-3">
        {messages.length === 0 && !streaming && (
          <div className="text-center text-sm text-muted-foreground py-8 px-2">
            <p>Frag den Assistenten, was du wissen willst.</p>
            <p className="mt-2 text-xs">
              z.B. „Liste die ersten 5 Kunden" oder „Welche Aufgaben sind offen?"
            </p>
          </div>
        )}
        {messages.map((m) => (
          <MessageView key={m.id} msg={m} />
        ))}
        {streaming && activeSteps.length > 0 && <StepList steps={activeSteps} />}
        {streaming && activeSteps.length === 0 && (
          <ThinkingIndicator label="verstehe Anfrage…" />
        )}
      </div>

      {/* Eingabe — Touch-Targets ≥ 48px auf Mobile, plus Drag&Drop fuer Bilder */}
      <div
        className={`border-t border-default p-2 bg-card transition-colors ${isDraggingOver ? 'bg-blue-50 dark:bg-blue-950/40 border-blue-400 dark:border-blue-700' : ''}`}
        onDragEnter={(e) => { e.preventDefault(); setIsDraggingOver(true); }}
        onDragOver={(e) => { e.preventDefault(); setIsDraggingOver(true); }}
        onDragLeave={(e) => {
          // dragleave feuert auch bei Hover-Wechsel auf Kinder — nur reagieren wenn der Cursor das Element wirklich verlaesst.
          if (e.currentTarget.contains(e.relatedTarget as Node)) return;
          setIsDraggingOver(false);
        }}
        onDrop={onDrop}
      >
        {/* Thumbnail-Leiste — nur wenn Bilder angehaengt */}
        {(attachments.length > 0 || uploadingAttachments) && (
          <div className="flex flex-wrap gap-2 mb-2">
            {attachments.map((a) => (
              <div key={a.id} className="relative group">
                <img
                  src={a.thumbnail}
                  alt="Anhang"
                  className="h-16 w-16 object-cover rounded border border-default"
                />
                <button
                  onClick={() => removeAttachment(a.id)}
                  aria-label="Anhang entfernen"
                  className="absolute -top-1.5 -right-1.5 h-6 w-6 rounded-full bg-card border border-default flex items-center justify-center text-foreground hover:bg-rose-100 dark:hover:bg-rose-900/40"
                >
                  <XIcon size={12} />
                </button>
              </div>
            ))}
            {uploadingAttachments && (
              <div className="h-16 w-16 rounded border border-default flex items-center justify-center text-muted-foreground">
                <Loader2 size={20} className="animate-spin" />
              </div>
            )}
          </div>
        )}

        <div className="flex items-end gap-2">
          {/* Anhang-Picker: capture=environment oeffnet auf Mobile die Kamera */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            capture="environment"
            onChange={onPickFiles}
            className="hidden"
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={streaming || attachments.length >= ATTACHMENT_LIMIT}
            aria-label="Bild anhängen"
            title={attachments.length >= ATTACHMENT_LIMIT ? `Maximal ${ATTACHMENT_LIMIT} Bilder pro Nachricht` : 'Bild anhängen (Drag & Drop, Einfügen, oder Datei wählen)'}
            className="h-14 w-14 md:h-10 md:w-10 rounded-md border border-default flex items-center justify-center text-muted-foreground hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed shrink-0"
          >
            <Paperclip size={22} className="md:w-[18px] md:h-[18px]" />
          </button>
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKey}
            onPaste={onPaste}
            placeholder={attachments.length > 0 ? 'Frage zum Bild…' : 'Frag den Assistenten…'}
            rows={2}
            disabled={streaming}
            className="flex-1 resize-none rounded-md border border-default bg-card text-foreground px-3 py-3 md:py-2 text-base md:text-sm focus:outline-none focus:ring-2 focus:ring-[color:var(--color-primary)] disabled:opacity-60"
          />
          <button
            onClick={() => void send()}
            disabled={streaming || (!input.trim() && attachments.length === 0)}
            aria-label="Senden"
            className="h-14 w-14 md:h-10 md:w-10 rounded-md text-white flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed shrink-0"
            style={{ backgroundColor: 'var(--color-primary)' }}
          >
            {streaming ? <Loader2 size={22} className="md:w-[18px] md:h-[18px] animate-spin" /> : <Send size={22} className="md:w-[18px] md:h-[18px]" />}
          </button>
        </div>
        <p className="text-[10px] text-muted-foreground mt-1 px-1">
          Enter sendet · Shift+Enter neue Zeile · Bilder drag&drop / einfügen / anhängen · {effectiveRole}
        </p>
      </div>
    </div>
  );
}

function MessageView({ msg }: { msg: ChatMessage }) {
  if (msg.role === 'user') {
    return (
      <div className="flex justify-end">
        <div
          className="max-w-[85%] rounded-lg text-white px-3 py-2 text-sm whitespace-pre-wrap"
          style={{ backgroundColor: 'var(--color-primary)' }}
        >
          {msg.content}
        </div>
      </div>
    );
  }
  if (msg.role === 'system') {
    return (
      <div className="text-xs italic text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 px-3 py-2 rounded">
        {msg.content}
      </div>
    );
  }
  return (
    <div className="space-y-1">
      {msg.steps && msg.steps.length > 0 && <StepList steps={msg.steps} compact />}
      <div className="max-w-[95%] rounded-lg bg-muted px-3 py-2 text-sm whitespace-pre-wrap text-foreground">
        {msg.content}
      </div>
    </div>
  );
}

/**
 * Tool-Indikator. Erfolgreiche Tools werden minimal angezeigt (Tester
 * 2026-05-15: JSON-Args + Treffer-Count im Chat ist Lärm). Fehlerfälle
 * dagegen voll mit Preview-Text, damit der User die Fehlermeldung
 * (z.B. Kunden-Vorschlagsliste) sieht — sonst bleibt nur „Fehler: 404"
 * im roten Banner.
 * Persistierte Messages (compact=true) zeigen gar nichts mehr — die
 * finale Antwort steht im Chat-Text.
 */
function StepList({ steps, compact = false }: { steps: Step[]; compact?: boolean }) {
  if (compact) return null;
  return (
    <div className="space-y-0.5">
      {steps.map((s, i) => (
        <div
          key={i}
          className={`flex items-start gap-1.5 text-[11px] px-2 py-1 rounded ${
            s.success === false
              ? 'bg-rose-500/10 text-rose-700 dark:text-rose-300'
              : s.success
                ? 'bg-brand-500/10 text-brand-700 dark:text-brand-300'
                : 'bg-muted text-muted-foreground'
          }`}
        >
          <Wrench size={11} className="mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="font-medium">
              {humanizeStep(s)}
            </div>
            {s.success === false && s.preview && (
              <div className="mt-0.5 text-[11px] whitespace-pre-wrap">{s.preview}</div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
